import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import { createTheme } from '@hivebrite/design-system/Theme';

import { ApiContextProvider } from 'hb-builder/api';
import Renderer from 'hb-builder/components/Renderer';
import { ComponentsProvider, DeviceModeProvider } from 'hb-builder/contexts';
import { useComponentsContext } from 'hb-builder/contexts/ComponentsContext';
import migrateDocument from 'hb-builder/utils/migrate';
import { pagesAndEmailsRoutes as routes } from 'hb-react/backoffice/components/BuilderWrapper/routes';
import { getPageComponentsList } from 'hb-react/backoffice/components/PageBuilder/utils/getPageComponentsList';
import { BUILDER_DOCUMENT_TYPE } from 'hb-react/backoffice/types/builder/builderDocument';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { convertNetworkColorsToFrontofficeTheme } from 'hb-react/style/convertNetworkColorsToFrontofficeTheme';
import { convertNetworkThemingToFrontofficeThemeVariables } from 'hb-react/style/convertNetworkThemingToFrontofficeThemeVariables';

const PageRendererDumb = ({
  app,
  document,
  href,
  ids,
  isMobilePreview,
  networkColors,
  networkFont,
  networkRootUrl,
  networkTheming,
  subdomainNetworkUrl,
  whitelabelName,
}) => {
  const [migratedDocument, setMigratedDocument] = useState();
  const { components } = useComponentsContext();
  const flags = useFeatureFlag();
  const { flagBuilderReworkButtons, flagsLoaded } = flags;

  const fontBrightness = networkTheming?.fo?.fo_outboxed_font_brightness;

  const theme = createTheme({
    customColors: convertNetworkColorsToFrontofficeTheme(networkColors),
    customThemeVariables: convertNetworkThemingToFrontofficeThemeVariables({
      /* @ts-expect-error untyped */
      theming: networkTheming,
      fontBrightness,
    }),
    themeName: 'fo',
  });

  useEffect(() => {
    const doc = migrateDocument({
      components,
      document,
      theme,
      type: BUILDER_DOCUMENT_TYPE.PAGE,
      flags: { flagBuilderReworkButtons, flagsLoaded },
    });

    setMigratedDocument(doc);
  }, []);

  if (isMobilePreview && href) {
    return <IFrame src={href} />;
  }

  if (!components || !migratedDocument) {
    return <Loader size={50} />;
  }

  return (
    <Renderer
      app={app}
      document={migratedDocument}
      ids={ids}
      isMobilePreview={isMobilePreview}
      networkColors={networkColors}
      networkFont={networkFont}
      networkRootUrl={networkRootUrl}
      networkTheming={networkTheming}
      subdomainNetworkUrl={subdomainNetworkUrl}
      whiteLabelUrl={whitelabelName}
    />
  );
};

PageRendererDumb.propTypes = {
  document: PropTypes.object.isRequired,
};

const PageRenderer = (props) => {
  const { contextId, topic } = props;
  const { flagBuilderResponsiveText, flagBuilderReworkButtons } =
    useFeatureFlag();
  const { currentNetwork, currentUser } = useDefaultAppContexts();
  const location = currentUser?.locations?.[0];
  const city = location?.city;
  const country = location?.country;

  const ids = useMemo(
    () => ({
      network: currentNetwork?.id,
      topic: topic?.id,
      user: currentUser?.id,
      userCity: city,
      userCountry: country,
    }),
    [currentNetwork?.id, topic?.id, currentUser?.id, city, country],
  );

  const components = getPageComponentsList({
    flagBuilderResponsiveText,
    flagBuilderReworkButtons,
    isPageEvent: contextId === 'page-event',
  });

  return (
    <ApiContextProvider authenticationIds={ids} routes={routes}>
      <ComponentsProvider components={components}>
        <DeviceModeProvider>
          <Wrapper>
            <Content>
              <PageRendererDumb
                {...props}
                ids={ids}
                whitelabelName={
                  topic && window.Hivebrite?.topic?.whitelabelName
                }
              />
            </Content>
          </Wrapper>
        </DeviceModeProvider>
      </ComponentsProvider>
    </ApiContextProvider>
  );
};

export default PageRenderer;

const Content = styled(Box).attrs(() => ({
  height: '100%',
  margin: '0 auto',
  maxWidth: '100vw',
  width: '100%',
}))``;

const IFrame = styled.iframe`
  width: 100%;
`;

const Wrapper = styled(Box).attrs(() => ({
  display: 'block',
  height: '100%',
  width: '100%',
}))``;
